package cz.lhoracek.mapl.ui.screens.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import cz.lhoracek.mapl.data.model.Session
import cz.lhoracek.mapl.data.model.SessionStatus
import cz.lhoracek.mapl.data.repository.SessionRepository
import cz.lhoracek.mapl.data.repository.currentTimeMillis
import cz.lhoracek.mapl.parseDeepLinkUrl
import kotlinx.coroutines.Job
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class HomeSessionItem(
    val session: Session,
    val roundsCount: Int,
    val latestRoundTimestamp: Long = 0L,
    val isPendingSync: Boolean = false,
)

class HomeViewModel(private val repository: SessionRepository) : ViewModel() {
    private companion object {
        const val DELETE_UNDO_TIMEOUT_MS = 4_000L
    }

    private val _isLoading = MutableStateFlow(true)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _recentlyJoinedSessionAt = MutableStateFlow<Map<String, Long>>(emptyMap())

    @OptIn(ExperimentalCoroutinesApi::class)
    val sessions: StateFlow<List<HomeSessionItem>> = combine(
        repository.observeSessions()
            .flatMapLatest { sessions -> sessions.withRoundsCount(repository) },
        repository.observeKnownSessionIds(),
        _recentlyJoinedSessionAt,
    ) { syncedSessions, knownSessionIds, recentlyJoinedAt ->
        val syncedById = syncedSessions.associateBy { it.session.id }
        val knownIds = knownSessionIds.toSet()
        val merged = knownSessionIds.map { sessionId ->
            syncedById[sessionId] ?: HomeSessionItem(
                session = Session(
                    id = sessionId,
                    name = sessionId,
                    baseBet = 0,
                    currentBet = 0,
                    status = SessionStatus.FINISHED,
                ),
                roundsCount = 0,
                isPendingSync = true,
            )
        } + syncedSessions.filter { it.session.id !in knownIds }

        merged.sortedByDescending { item ->
            maxOf(item.activityTimestamp, recentlyJoinedAt[item.session.id] ?: 0L)
        }
    }
        .onEach { _isLoading.value = false }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    private val _joinResult = MutableStateFlow<JoinResult?>(null)
    val joinResult: StateFlow<JoinResult?> = _joinResult.asStateFlow()

    private val _pendingDeleteSessions = MutableStateFlow<Map<String, HomeSessionItem>>(emptyMap())
    val pendingDeleteCount: StateFlow<Int> = _pendingDeleteSessions
        .map { it.size }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), 0)

    private val _pendingDeleteSnackbarToken = MutableStateFlow(0)
    val pendingDeleteSnackbarToken: StateFlow<Int> = _pendingDeleteSnackbarToken.asStateFlow()

    private val _undoSessionIds = MutableStateFlow<Set<String>>(emptySet())
    val undoSessionIds: StateFlow<Set<String>> = _undoSessionIds.asStateFlow()

    private val deleteTimeoutJobs = mutableMapOf<String, Job>()

    fun endSession(sessionId: String) {
        viewModelScope.launch { repository.endSession(sessionId) }
    }

    fun markForDelete(session: HomeSessionItem) {
        val sessionId = session.session.id
        if (_pendingDeleteSessions.value.containsKey(sessionId)) return

        _pendingDeleteSessions.update { it + (sessionId to session) }
        _pendingDeleteSnackbarToken.update { it + 1 }

        deleteTimeoutJobs[sessionId] = viewModelScope.launch {
            delay(DELETE_UNDO_TIMEOUT_MS)
            commitDelete(sessionId, cancelTimeoutJob = false)
        }
    }

    fun commitDelete() {
        _pendingDeleteSessions.value.keys.toList()
            .forEach { sessionId ->
                commitDelete(sessionId, cancelTimeoutJob = true)
            }
    }

    fun cancelDelete() {
        val restoredIds = _pendingDeleteSessions.value.keys.toList()
        if (restoredIds.isEmpty()) return

        restoredIds.forEach { sessionId ->
            deleteTimeoutJobs.remove(sessionId)?.cancel()
        }
        _pendingDeleteSessions.value = emptyMap()
        _undoSessionIds.update { it + restoredIds }
    }

    fun clearUndoSignal(sessionId: String) {
        _undoSessionIds.update { it - sessionId }
    }

    fun joinSession(sessionId: String) {
        val normalized = sessionId.trim()
        if (normalized.isBlank()) {
            _joinResult.value = JoinResult.NotFound
            return
        }
        viewModelScope.launch {
            val ok = repository.joinSession(normalized)
            if (ok) {
                _recentlyJoinedSessionAt.update { it + (normalized to currentTimeMillis()) }
            }
            _joinResult.value = if (ok) JoinResult.Success(normalized) else JoinResult.NotFound
        }
    }

    fun joinByUrl(url: String) {
        val sessionId = parseDeepLinkUrl(url) ?: url.trim()
        if (sessionId.isNotBlank()) joinSession(sessionId)
    }

    fun consumeJoinResult() { _joinResult.value = null }

    private fun commitDelete(sessionId: String, cancelTimeoutJob: Boolean) {
        if (!_pendingDeleteSessions.value.containsKey(sessionId)) return

        _pendingDeleteSessions.update { it - sessionId }
        if (cancelTimeoutJob) {
            deleteTimeoutJobs.remove(sessionId)?.cancel()
        } else {
            deleteTimeoutJobs.remove(sessionId)
        }

        _recentlyJoinedSessionAt.update { it - sessionId }
        viewModelScope.launch {
            repository.removeKnownSession(sessionId)
        }
    }
}

private fun List<Session>.withRoundsCount(
    repository: SessionRepository,
): Flow<List<HomeSessionItem>> {
    if (isEmpty()) return flowOf(emptyList())

    return combine(
        map { session ->
            repository.observeRounds(session.id).map { rounds ->
                HomeSessionItem(
                    session = session,
                    roundsCount = rounds.size,
                    latestRoundTimestamp = rounds.maxOfOrNull { it.timestamp } ?: 0L,
                )
            }
        }
    ) { items ->
        items.toList()
    }
}

private val HomeSessionItem.activityTimestamp: Long
    get() = maxOf(session.createdAt, latestRoundTimestamp)

sealed interface JoinResult {
    data class Success(val sessionId: String) : JoinResult
    data object NotFound : JoinResult
}
